import {Observable, Subscription} from 'rxjs';
import {debounceTime, filter, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {shuffle} from 'lodash';
import {formConfig} from './brain-research.data';
import {FADE_IN} from '../../../../shared/animations';
import {IncrementExploreStep, InitForm, UpdateData, UpdateExploreStep} from '../../../states/explore/explore.actions';
import {ExploreState} from '../../../states/explore/explore.state';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatSlider } from '@angular/material/slider';
import { MatCheckbox } from '@angular/material/checkbox';
import { AsyncPipe } from '@angular/common';


@Component({
    selector: 'app-brain-research',
    templateUrl: './brain-research.component.html',
    styleUrls: ['./brain-research.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [FADE_IN],
    imports: [FormsModule, ReactiveFormsModule, MatTabGroup, MatTab, MatSlider, MatCheckbox, AsyncPipe]
})
export class BrainResearchComponent implements OnInit, OnDestroy {
  public readonly dataLoaded$ = this.store.select(ExploreState.exploreData);
  public readonly loading$ = this.store.select(ExploreState.isLoading);
  public readonly loaded$ = this.store.select(ExploreState.isLoaded);
  public readonly step$ = this.store.select(ExploreState.step);

  isDone$: Observable<boolean>;
  ready$: Observable<boolean>;

  formConfig = [];
  formVersion = 1;
  selectedMask = [];

  private formSubscription = null;
  private subscription = new Subscription();

  questionCount = 0;
  step = 0;
  form = null;

  constructor(
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
  }

  ngOnInit(): void {
    this.formConfig = shuffle(formConfig);

    this.store.dispatch(new InitForm(this.formConfig));

    this.isDone$ = this.step$.pipe(
      map(step => step >= this.questionCount)
    );

    this.ready$ = this.loaded$.pipe(filter(x => !!x));

    this.subscription.add(
      this.dataLoaded$.subscribe(data => {
        this.initForm(data);
      })
    );
    this.subscription.add(
      this.step$.subscribe(step => {
        this.step = step;
      })
    );
  }

  initForm(initialData) {
    this.unsubscribeForm();

    const formParam = {};
    this.selectedMask = [];
    this.formConfig.forEach((configLine, index) => {
      if (configLine.type === 'checkbox') {
        const subParam = {};
        for (const choice of configLine.choices) {
          const defaultValue = initialData && configLine['name'] in initialData && choice in initialData[configLine['name']] ?
            initialData[configLine['name']][choice] : configLine['value'];
          subParam[choice] = defaultValue;
        }
        this.selectedMask[index] = true;
        formParam[configLine['name']] = this.fb.group(subParam);
      } else {
        const defaultValue = initialData && configLine['name'] in initialData ?
          initialData[configLine['name']] : configLine['value'];
        this.selectedMask[index] = initialData && configLine['name'] in initialData;
        formParam[configLine['name']] = defaultValue;
      }
    });
    this.questionCount = Object.keys(formParam).length;
    this.form = this.fb.group(formParam);
    this.cd.detectChanges();

    this.formSubscription = this.form.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(
      data => {
        // Only use selected values
        this.selectedMask[this.step] = true;
        const keys = this.formConfig.map(obj => obj.name).filter((_, index) => this.selectedMask[index]);
        const filteredData = keys.reduce((obj, key) => {
          if (key in data) {
            obj[key] = data[key];
          }
          return obj;
        }, {});
        this.store.dispatch(
          new UpdateData({...filteredData, version: this.formVersion})
        );
      }
    );
  }

  unsubscribeForm() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
      this.formSubscription = null;
    }
  }

  ngOnDestroy() {
    this.unsubscribeForm();
    this.subscription.unsubscribe();
  }

  rewind() {
    this.store.dispatch(new UpdateExploreStep(0));
  }

  next(triggerFormUpdate = false) {
    this.selectedMask[this.step] = true;
    if (triggerFormUpdate && this.form) {
      this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
    }
    this.store.dispatch(new IncrementExploreStep());
  }

  skip() {
    this.store.dispatch(new IncrementExploreStep());
  }

  getChoicesText(configLine, i) {
    return 'choicesText' in configLine ?
      configLine.choicesText[i] : configLine.choices[i];
  }

  getCheckboxFormGroup(name) {
    return this.form.controls[name];
  }

  getRadioButtonId(name, choice) {
    return `radio_${name}_${choice}`;
  }

  hasNextButton(tabStep: number) {
    return tabStep < this.questionCount && this.formConfig[tabStep].type !== 'radio';
  }

  back() {
    this.router.navigate(['../'], {relativeTo: this.route});
  }
}
